import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tarjeta } from "@/components/ConfigComunes";
import { useConfigDevices } from "@/hooks/useConfig";
import type { ConfigDevice } from "@/api/types";

/**
 * Qué hardware hay de verdad en la flota de rutelas, y en qué versión.
 *
 * Hasta el 7-sep-2026 el inventario decía `RUT240` para las 37: eran 16 RUT200
 * con el valor por omisión copiado a todas. Ahora AROC deduce el modelo del
 * prefijo del firmware en cada cosecha, y esta tarjeta es donde se ve.
 *
 * Deliberadamente NO es una tabla de equipos: es el agregado. Un listado de 37
 * renglones no contesta la pregunta que importa —"¿qué tengo y en cuántas
 * versiones?"—, y esta página ya tiene la tabla de deriva para el detalle.
 */

const TIPO_RUT = "rut240_teltonika";

//: Los que aún no han cosechado desde que existe la columna. No es un error:
//: es "todavía no lo sabemos", y se distingue de un modelo conocido a propósito.
const SIN_DATO = "sin dato";

type Fila = {
  modelo: string;
  equipos: number;
  versiones: Array<[string, number]>;
};

export function agruparFlota(devices: ConfigDevice[]): {
  filas: Fila[];
  sinDato: number;
  total: number;
} {
  const porModelo = new Map<string, Map<string, number>>();
  let sinDato = 0;

  for (const d of devices) {
    if (!d.firmware_version) {
      sinDato += 1;
      continue;
    }
    const modelo = d.model ?? SIN_DATO;
    const versiones = porModelo.get(modelo) ?? new Map<string, number>();
    versiones.set(d.firmware_version, (versiones.get(d.firmware_version) ?? 0) + 1);
    porModelo.set(modelo, versiones);
  }

  const filas = [...porModelo.entries()]
    .map(([modelo, versiones]) => ({
      modelo,
      equipos: [...versiones.values()].reduce((a, b) => a + b, 0),
      versiones: [...versiones.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])),
    }))
    .sort((a, b) => b.equipos - a.equipos || a.modelo.localeCompare(b.modelo));

  return { filas, sinDato, total: devices.length };
}

export function FlotaRutPanel({ cliente }: { cliente?: string } = {}) {
  const { data, isLoading } = useConfigDevices({
    device_type: TIPO_RUT,
    // Cada pestaña cuenta sólo las rutelas de su cliente (Holanda / tiLatina).
    client: cliente,
    per_page: 200, // las 37 caben en una página; sin esto se corta en 50
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Cargando la flota…</p>;
  }

  const devices = data?.items ?? [];
  if (devices.length === 0) return null;

  const { filas, sinDato, total } = agruparFlota(devices);
  const conFirmware = total - sinDato;
  const coberturaPct = total > 0 ? Math.round((100 * conFirmware) / total) : 0;

  return (
    <div className="space-y-4">
      {/* Cabecera de cobertura, con el mismo estilo de tarjeta que el resto.
          Aquí NO hay golden: los rutelas se inventarían por firmware, así que
          la cobertura mide cuántos tienen su firmware leído, no cumplimiento. */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tarjeta
          titulo="Cobertura"
          valor={`${coberturaPct}%`}
          nota={`${conFirmware} de ${total} con firmware leído`}
          alerta={coberturaPct < 80}
        />
        <Tarjeta titulo="Rutelas" valor={total} />
        <Tarjeta
          titulo="Sin dato"
          valor={sinDato}
          nota="firmware no leído aún"
          alerta={sinDato > 0}
        />
        <Tarjeta titulo="Modelos" valor={filas.length} />
      </div>

      <div className="rounded-md border bg-card p-4">
        <h3 className="mb-1 text-sm font-medium">
          Flota de rutelas — modelo y firmware
        </h3>
      <p className="mb-3 text-xs text-muted-foreground">
        El modelo se deduce del prefijo del firmware, que es de fábrica:{" "}
        <code>RUT2M_R_</code> es un RUT200 y <code>RUT2_R_</code> un RUT240.
      </p>

      {filas.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Modelo</TableHead>
              <TableHead className="text-right">Equipos</TableHead>
              <TableHead>Versiones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filas.map((f) => (
              <TableRow key={f.modelo}>
                <TableCell className="font-medium">{f.modelo}</TableCell>
                <TableCell className="text-right">{f.equipos}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {f.versiones.map(([v, n]) => `${v} (${n})`).join(" · ")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {sinDato > 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          <span className="font-medium">{sinDato}</span> de {total} sin dato todavía:
          el firmware se lee en la cosecha, así que aparecen en cuanto vuelvan a
          cosechar (el barrido automático corre a las 04:00).
        </p>
      )}
      </div>
    </div>
  );
}
